import React, { useCallback, useEffect, useRef, useState } from 'react';
import { cloneDeep, isNil } from 'lodash-es';
import {
  devLog,
  isProd,
  getMobileOS,
  isMobile,
  isSessionStorageAvailable
} from '@strava/utils';
import { MOBILE_OS } from '@strava/constants';

import { isFalsePositive } from './useBranchIO.hook.helpers';

import { type init, type data, type link, type BranchError } from 'branch-sdk';
declare global {
  interface Window {
    branch?: {
      init: typeof init;
      data: typeof data;
      link: typeof link;
    };
  }
}

type ErrorLogger = (error: BranchError, captureContext: object) => void;

interface UseBranchIOProps {
  cookiesAccepted?: boolean;
  logError?: ErrorLogger;
  shouldUseLegacyBranch?: boolean;
  dataSharingOptedOut?: boolean;
  defaultAppLink?: string;
  externalIdentityHash?: string;
}

/**
 * This hook initializes branch web SDK and exposes different API endpoints
 * for transacting with the SDK.
 * */
export const useBranchIO = ({
  cookiesAccepted: _cookiesAccepted, // note: branch is not initialized when cookies are denied
  logError: _logError,
  shouldUseLegacyBranch = false, //MFEs mounted in active are expected to use an instance of window.branch from legacy branch SDK
  dataSharingOptedOut,
  externalIdentityHash,
  defaultAppLink = '/mobile'
}: UseBranchIOProps = {}) => {
  const [hasApp, setHasApp] = useState(false);
  const [appLink, setAppLink] = useState<string | null>(defaultAppLink);
  const [error, setError] = useState<Error | string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);
  const [identityHash, setIdentityHash] = useState<string | null>(null);

  const [cookiesAccepted, setCookiesAccepted] = useState(_cookiesAccepted);

  /**
   * Legacy active _branch_analytics.haml and nextJS CookieBanner transmits
   * BranchInitialized event when cookies are accepted and branch is initialized.
   * When this happens, useBranchIO.hook is
   * triggered to update hasApp and appLink values.
   */
  useEffect(() => {
    window.addEventListener('AcceptCookies', () => setCookiesAccepted(true));
    return () => {
      window.removeEventListener('AcceptCookies', () => {});
    };
  }, []);

  const appLinkGenerated = useRef(false);

  // make startTransition compatible with React 17 and below
  // since this hook is used on MFEs that are mounted in active (react16)
  const startTransitionCompat = (scope: (...args: any) => any) => {
    // @ts-ignore: only for >= React18
    const { startTransition } = React;

    if (typeof startTransition !== 'function') {
      scope();
    } else {
      startTransition(scope);
    }
  };

  // override logError to include hook specific error logging rules
  const logError = useCallback(
    (e: any, context = {}) => {
      const captureContext = {
        contexts: {
          'branch-sdk': {
            ...context,
            hasApp,
            isProcessing,
            shouldUseLegacyBranch,
            dataSharingOptedOut,
            cookiesAccepted,
            'navigator.cookiesAccepted': navigator?.cookieEnabled
          }
        },
        tags: {
          'hook.name': 'use-branch-io'
        }
      };

      if (_logError && !isFalsePositive(e.message)) {
        _logError(e, captureContext);
      }
    },
    // deps are ignored on purpose to avoid unnecessary re-renders since this callback
    // is functioning as a util function.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const createLink = useCallback(
    ({
      feature = 'unknown',
      data,
      isLoggedIn = false
    }: {
      feature?: string;
      data?: Record<string, any>;
      isLoggedIn?: boolean;
    } = {}) => {
      // only generate a appLink once
      if (appLinkGenerated.current === true) {
        // an appLink is meant to be generated once per page load and
        // shared across components through `BranchContext` in @strava/container-context
        return;
      }

      const linkData = {
        channel: isMobile() ? 'mobile_web' : 'web',
        feature,
        tags: data?.tags,
        data
      };

      if (!error && window.branch) {
        // cloning the props avoids unnecessary re-renders since branch updates this hash
        const clonedLinkData = cloneDeep(linkData);

        /**
         * linkData.data
         *  - add default strava_deeplink_url if not present
         *  - add fallback desktop url
         */
        if (isNil(clonedLinkData.data)) {
          clonedLinkData.data = {};
        }
        if (isNil(clonedLinkData.data.strava_deeplink_url)) {
          clonedLinkData.data.strava_deeplink_url = 'strava://';
        }

        // android and ios requires for this switch to be set to true
        // when request is made from logged out pages. This avoids
        // infinite login attempts
        // see - https://github.com/strava/android/pull/20088
        if (!isLoggedIn) {
          clonedLinkData.data.logged_out_deeplink = true;
        }

        clonedLinkData.data.$desktop_url = `${window.location.origin}/register`;

        window.branch.link(clonedLinkData, (err, link) => {
          startTransitionCompat(() => {
            if (err) {
              logError(err, {
                method: 'createLink',
                linkData: JSON.stringify(clonedLinkData),
                'linkData.tags': JSON.stringify(clonedLinkData?.tags)
              });
              setError(err);
            }
            devLog(`[useBranchIO] successfully created appLink - ${link}`);
            appLinkGenerated.current = true;
            setAppLink(link);
          });
        });
      } else {
        devLog('[useBranchIO] appLink not created');
      }
    },
    [error, logError, setError]
  );

  const setIdentity = useCallback(
    (externalIdentityHash?: string) => {
      if (externalIdentityHash) {
        window.branch.setIdentity(externalIdentityHash, (err, data) => {
          if (err) {
            logError(err, { method: 'setIdentity' });
            setError(err);
            return;
          }
          if (data) {
            setIdentityHash(externalIdentityHash);
          }
        });
      } else {
        window.branch.logout((err) => {
          if (err) {
            logError(err, { method: 'logout' });
            setError(err);
            return;
          }

          setIdentityHash(null);
        });
      }
    },
    [logError, setError]
  );

  const processBranchData = useCallback(
    (err: any, data: any, method: any) => {
      if (err) {
        logError(err, { method });
        setError(err);
      } else if (data?.['has_app']) {
        setHasApp(true);
      }

      setIsProcessing(false);
    },
    [logError, setError, setHasApp, setIsProcessing]
  );

  useEffect(() => {
    if (!_logError) {
      startTransitionCompat(() => {
        const e = new Error('hook requires `logError` callback');
        devLog(e.message);
        setError(e);
      });
      return;
    }

    if (!cookiesAccepted) {
      startTransitionCompat(() => {
        // use direct link to app store if user has not accepted cookies (branch is not initialized)
        const os = getMobileOS();
        if (os === MOBILE_OS.android) {
          setAppLink(
            'https://play.google.com/store/apps/details?id=com.strava'
          );
        } else if (os === MOBILE_OS.iPhone) {
          setAppLink(
            'https://itunes.apple.com/app/strava-cycling/id426826309?mt=8'
          );
        }

        setIsProcessing(false);
      });
      return;
    }

    if (window.branch) {
      devLog('[useBranchIO] reusing an already installed instance of branch');
      window.branch.data((err, data) => {
        startTransitionCompat(() => {
          setIsProcessing(true);
          processBranchData(err, data, 'data');
        });
      });
    } else if (!shouldUseLegacyBranch) {
      //branch was not found on page. Initializing a new shared instance...
      startTransitionCompat(() => {
        setIsProcessing(true);
      });

      const initBranchSDK = async () => {
        // dynamically import branch-sdk
        // this is necessary for nextJS SSR since useEffect hook are always executed on the client-side
        // therefore the window object will be available for the code inside those methods.
        window.branch = (await import('branch-sdk')).default;

        const key = isProd()
          ? 'key_live_lmpPsfj2DP8CflI4rmzfiemerte7sgwm'
          : 'key_test_dfiHrehXENYvpoG6DjClFljaBvf0DjrE';

        const options = {
          retries: 3,
          tracking_disabled: false,
          metadata: { external_identity_version: 5, next: true } //used as a filter when debugging events on branch.io dashboard
        };

        if (dataSharingOptedOut) {
          options.tracking_disabled = true;
        }

        // clean legacy identities (non-hashed athlete ids)
        var branchSessionIdentity = isSessionStorageAvailable()
          ? JSON.parse(sessionStorage.getItem('branch_session') || '{}')
              .identity
          : null;

        if (branchSessionIdentity && branchSessionIdentity.length !== 64) {
          devLog(
            `[useBranchIO] clearing legacy branch session identity - ${branchSessionIdentity}`
          );
          sessionStorage.removeItem('branch_session');
        }

        window.branch?.init(key, options, (err, data) => {
          var identity = data?.identity;
          if (externalIdentityHash && identity && identity.length !== 64) {
            // for debugging - fire session reset event
            window.branch.logEvent('web_session_reset', { identity });

            window.branch.logout((err) => {
              if (err) {
                logError(err, { method: 'logout' });
                setError(err);
                return;
              }
              // for debugging - log the identity that was cleared
              logError(
                new Error(
                  '[useBranchIO] clearing legacy branch session identity'
                ),
                {
                  data_identity: identity,
                  session_identity: branchSessionIdentity,
                  external_identity: externalIdentityHash
                }
              );
            });
          }
          startTransitionCompat(() => {
            processBranchData(err, data, 'init');
            setIdentity(externalIdentityHash);
          });
        });
      };

      initBranchSDK();
    } else {
      logError(
        new Error(
          '[useBranchIO] need event listener for BranchInitialized to call handleLegacyBranchInit'
        )
      );
      startTransitionCompat(() => {
        setIsProcessing(false);
      });
    }
  }, [
    _logError,
    cookiesAccepted,
    processBranchData,
    dataSharingOptedOut,
    shouldUseLegacyBranch,
    logError,
    externalIdentityHash,
    setIdentity
  ]);

  // if we have to wait for active to initialize branch
  const handleLegacyBranchInit = useCallback(
    (event: { detail: { err: any; data: any } }) => {
      const { err, data } = event.detail;
      processBranchData(err, data, 'legacy_init');
    },
    [processBranchData]
  );

  return {
    isProcessing,
    hasApp,
    handleLegacyBranchInit,
    setIdentity,
    identityHash,
    createLink,
    error,
    appLink,
    isReady: !isProcessing && !error && cookiesAccepted //branch is ready when cookies are accepted and the sdk is initialized successfully
  };
};

export default useBranchIO;
